import { i18n } from '@/shared/i18n'

interface ConvertOptions {
  maxWidth?: number
  maxHeight?: number
  quality?: number
}

const DEFAULT_MAX_WIDTH = 1920
const DEFAULT_MAX_HEIGHT = 1080
const DEFAULT_QUALITY = 0.85

export function supportsWebpEncoding(): boolean {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  return canvas.toDataURL('image/webp').startsWith('data:image/webp')
}

function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight }
  }
  const widthRatio = maxWidth / originalWidth
  const heightRatio = maxHeight / originalHeight
  const ratio = Math.min(widthRatio, heightRatio)
  return {
    width: Math.round(originalWidth * ratio),
    height: Math.round(originalHeight * ratio),
  }
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.addEventListener('load', () => {
      URL.revokeObjectURL(url)
      resolve(img)
    })
    img.addEventListener('error', () => {
      URL.revokeObjectURL(url)
      reject(new Error(i18n.t('common.errors.failedToLoadImage')))
    })
    img.src = url
  })
}

function convertToBlob(
  img: HTMLImageElement,
  width: number,
  height: number,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new Error(i18n.t('common.errors.failedToProcessImage')))
      return
    }
    ctx.drawImage(img, 0, 0, width, height)
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error(i18n.t('common.errors.failedToProcessImage')))
        }
      },
      mimeType,
      quality,
    )
  })
}

export async function convertImageToWebp(file: File, options?: ConvertOptions): Promise<Blob> {
  const maxWidth = options?.maxWidth ?? DEFAULT_MAX_WIDTH
  const maxHeight = options?.maxHeight ?? DEFAULT_MAX_HEIGHT
  const quality = options?.quality ?? DEFAULT_QUALITY

  const img = await loadImageFromFile(file)
  const { width, height } = calculateDimensions(img.width, img.height, maxWidth, maxHeight)
  const mimeType = supportsWebpEncoding() ? 'image/webp' : 'image/jpeg'
  return convertToBlob(img, width, height, mimeType, quality)
}
